import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, addDoc, updateDoc, doc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import Swal from "sweetalert2";
import { isMobileDevice } from "../utils/deviceDetection";

const AttendanceModal = ({ show, onClose, userEmail, userId, userName }) => {
  const [loginTime, setLoginTime] = useState("");
  const [logoutTime, setLogoutTime] = useState("");
  const [breaks, setBreaks] = useState([]);
  const [activeBreak, setActiveBreak] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [attendanceStarted, setAttendanceStarted] = useState(false);
  const [attendanceRecordId, setAttendanceRecordId] = useState(null);
  const [attendanceSubmitted, setAttendanceSubmitted] = useState(false);
  const [savedTotalWorkMinutes, setSavedTotalWorkMinutes] = useState(null); // Store database value for submitted attendance
  const [showRegularizationInput, setShowRegularizationInput] = useState(false);
  const [regularizationReason, setRegularizationReason] = useState("");
  const [regularizationStatus, setRegularizationStatus] = useState(null); // null, 'pending', 'approved', 'rejected'
  const [submittingRegularization, setSubmittingRegularization] = useState(false);

  // Get current local system time
  const getLocalTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Check if accessed from mobile device and prevent access
  useEffect(() => {
    if (show && isMobileDevice()) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Mark Attendance is not available on mobile devices",
        text: "Please use a desktop or laptop computer to mark attendance.",
        timer: 4000,
      });
      onClose();
      return;
    }
  }, [show, onClose]);

  // Check for existing attendance and initialize when modal opens
  useEffect(() => {
    if (show && !hasInitialized) {
      const initializeAttendance = async () => {
        // Get current date from local system
        const today = new Date().toISOString().split("T")[0];

        // First, check for any previous day's attendance without logout (Missing Logout)
        try {
          const previousDaysQuery = query(
            collection(db, "attendance"),
            where("userId", "==", userId),
            where("logoutTime", "==", null)
          );

          const previousDocs = await getDocs(previousDaysQuery);
          for (const docSnap of previousDocs.docs) {
            const record = docSnap.data();
            // If this is from a previous day (not today), mark as Missing Logout
            if (record.date && record.date !== today && !record.missingLogout) {
              await updateDoc(doc(db, "attendance", docSnap.id), {
                logoutTime: "00:00",
                missingLogout: true,
                halfDayPenalty: true,
                totalWorkMinutes: 0, // Mark as incomplete
                updatedAt: serverTimestamp()
              });
              console.log(`Marked attendance ${docSnap.id} as Missing Logout`);
            }
          }
        } catch (error) {
          console.error("Error checking for missing logout:", error);
        }

        // Check if attendance already started for today
        try {
          const attendanceQuery = query(
            collection(db, "attendance"),
            where("userId", "==", userId),
            where("date", "==", today)
          );

          const existingDocs = await getDocs(attendanceQuery);
          if (!existingDocs.empty) {
            const existingRecord = existingDocs.docs[0].data();
            setAttendanceRecordId(existingDocs.docs[0].id);
            setLoginTime(existingRecord.loginTime || "");
            setLogoutTime(existingRecord.logoutTime || "");
            setBreaks(existingRecord.breaks || []);
            setAttendanceStarted(true);

            // Check if attendance is already submitted (has logoutTime)
            if (existingRecord.logoutTime) {
              setAttendanceSubmitted(true);
              // Use the saved totalWorkMinutes from database for display
              if (existingRecord.totalWorkMinutes !== undefined) {
                setSavedTotalWorkMinutes(existingRecord.totalWorkMinutes);
              }
              // Check regularization status
              if (existingRecord.regularization) {
                setRegularizationStatus(existingRecord.regularization.status);
                setRegularizationReason(existingRecord.regularization.reason || "");
              }
            } else {
              // Check if there's an active break (only if not submitted)
              if (existingRecord.breaks && existingRecord.breaks.length > 0) {
                const lastBreak = existingRecord.breaks[existingRecord.breaks.length - 1];
                if (lastBreak.start && !lastBreak.end) {
                  setActiveBreak(lastBreak);
                }
              }
            }
          } else {
            // No existing attendance, fetch current time for login
            const internetTime = getLocalTime();
            if (internetTime) {
              setLoginTime(internetTime);
            }
          }
        } catch (error) {
          console.error("Error checking existing attendance:", error);
          // Still set time even if check fails
          const internetTime = getLocalTime();
          if (internetTime) {
            setLoginTime(internetTime);
          }
        }
        setHasInitialized(true);
      };
      initializeAttendance();
    }
  }, [show, hasInitialized, userId]);

  // Refresh time every 50 seconds while modal is open (only before attendance starts)
  useEffect(() => {
    if (!show || attendanceStarted) return;

    // Initial fetch
    const refreshTime = () => {
      const internetTime = getLocalTime();
      if (internetTime) {
        setLoginTime(internetTime);
      }
    };

    // Set up interval to refresh every 50 seconds
    const intervalId = setInterval(refreshTime, 50000); // 50 seconds

    // Cleanup interval when modal closes
    return () => {
      clearInterval(intervalId);
    };
  }, [show, attendanceStarted]);

  // Auto-update end time when login time or breaks change (login + 9:30 hours shift = 8:30 work + 1 hour break)
  // End time is capped at midnight (00:00) - shift cannot extend past 12 AM
  useEffect(() => {
    if (attendanceStarted && loginTime) {
      const breakMinutes = calculateTotalBreakMinutes();
      const login = parseTime(loginTime);

      // Add 9.5 hours (570 minutes) shift duration + any additional break time
      const totalMinutes = 570 + breakMinutes;
      const expectedEnd = new Date(login.getTime() + totalMinutes * 60 * 1000);

      // Check if end time goes past midnight (next day)
      const loginDate = new Date(login);
      const midnightToday = new Date(loginDate);
      midnightToday.setHours(24, 0, 0, 0); // Set to midnight (00:00 of next day)

      let expectedEndTime;
      if (expectedEnd >= midnightToday) {
        // Cap at midnight
        expectedEndTime = "00:00";
      } else {
        // Format as HH:MM
        const hours = String(expectedEnd.getHours()).padStart(2, '0');
        const minutes = String(expectedEnd.getMinutes()).padStart(2, '0');
        expectedEndTime = `${hours}:${minutes}`;
      }

      setLogoutTime(expectedEndTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginTime, breaks, attendanceStarted]);

  const handleStartBreak = async () => {
    if (activeBreak) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please end current break first",
        timer: 2000,
      });
      return;
    }

    // Get current local time
    const internetTime = getLocalTime();

    const newBreak = {
      start: internetTime,
      end: null,
      type: "Break", // Default type
    };
    const updatedBreaks = [...breaks, newBreak];
    setActiveBreak(newBreak);
    setBreaks(updatedBreaks);

    // If attendance is started, save breaks to database
    if (attendanceStarted && attendanceRecordId) {
      try {
        await updateDoc(doc(db, "attendance", attendanceRecordId), {
          breaks: updatedBreaks,
        });
      } catch (error) {
        console.error("Error updating breaks:", error);
        // Don't show error to user, breaks are still updated locally
      }
    }
  };

  const handleEndBreak = async () => {
    if (!activeBreak) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "No active break to end",
        timer: 2000,
      });
      return;
    }

    // Get current local time
    const internetTime = getLocalTime();

    const updatedBreaks = breaks.map((b, index) => {
      if (index === breaks.length - 1 && !b.end) {
        return { ...b, end: internetTime };
      }
      return b;
    });

    setBreaks(updatedBreaks);
    setActiveBreak(null);

    // If attendance is started, save breaks to database
    if (attendanceStarted && attendanceRecordId) {
      try {
        await updateDoc(doc(db, "attendance", attendanceRecordId), {
          breaks: updatedBreaks,
        });
      } catch (error) {
        console.error("Error updating breaks:", error);
        // Don't show error to user, breaks are still updated locally
      }
    }
  };

  const parseTime = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const calculateTotalBreakMinutes = () => {
    return breaks.reduce((total, b) => {
      if (b.start && b.end) {
        const start = parseTime(b.start);
        const end = parseTime(b.end);
        const diff = (end - start) / (1000 * 60); // minutes
        return total + Math.max(0, diff);
      }
      return total;
    }, 0);
  };

  const calculateTotalTimeMinutes = () => {
    if (!loginTime || !logoutTime) return 0;

    const login = parseTime(loginTime);
    const logout = parseTime(logoutTime);

    // Handle case where logout is next day
    if (logout < login) {
      logout.setDate(logout.getDate() + 1);
    }

    return (logout - login) / (1000 * 60);
  };

  const calculateTotalWorkMinutes = () => {
    if (!loginTime || !logoutTime) return 0;

    const totalMinutes = calculateTotalTimeMinutes();
    const breakMinutes = calculateTotalBreakMinutes();

    return Math.max(0, totalMinutes - breakMinutes);
  };

  // Handle Start Attendance
  const handleStartAttendance = async () => {
    // Prevent attendance on mobile devices
    if (isMobileDevice()) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Mark Attendance is not available on mobile devices",
        text: "Please use a desktop or laptop computer to mark attendance.",
        timer: 4000,
      });
      return;
    }

    if (!loginTime) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Login time is required",
        timer: 2000,
      });
      return;
    }

    setSubmitting(true);

    try {
      // Get current date from local system
      const today = new Date().toISOString().split("T")[0];

      // Check if attendance already exists
      const attendanceQuery = query(
        collection(db, "attendance"),
        where("userId", "==", userId),
        where("date", "==", today)
      );

      const existingDocs = await getDocs(attendanceQuery);
      if (!existingDocs.empty) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: "Attendance already started for today",
          timer: 2000,
        });
        setSubmitting(false);
        return;
      }

      // Create attendance record with login time only
      const docRef = await addDoc(collection(db, "attendance"), {
        userId,
        userEmail,
        userName: userName || userEmail?.split("@")[0] || "User",
        date: today,
        loginTime,
        logoutTime: null,
        breaks: [],
        totalWorkMinutes: 0,
        missingLogout: false,
        halfDayPenalty: false,
        createdAt: serverTimestamp(),
      });

      setAttendanceRecordId(docRef.id);
      setAttendanceStarted(true);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Attendance started successfully",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error starting attendance:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to start attendance",
        text: error.message,
        timer: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Finish Attendance
  const handleFinishAttendance = async () => {
    // Prevent attendance on mobile devices
    if (isMobileDevice()) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Mark Attendance is not available on mobile devices",
        text: "Please use a desktop or laptop computer to mark attendance.",
        timer: 4000,
      });
      return;
    }

    // Check if there's an active break
    if (activeBreak) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please end your break before finishing",
        timer: 2000,
      });
      return;
    }

    // Get current local time as finish time
    const currentInternetTime = getLocalTime();

    // Set the finish time to current time
    if (currentInternetTime) {
      setLogoutTime(currentInternetTime);
    }

    // Use currentInternetTime as logoutTime for calculations
    const finishTime = currentInternetTime || logoutTime;

    if (!finishTime) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Unable to get current time",
        timer: 2000,
      });
      return;
    }

    // Validate finish time is not before login time
    const login = parseTime(loginTime);
    const logout = parseTime(finishTime);
    if (logout < login) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Finish time cannot be before login time",
        timer: 2000,
      });
      return;
    }

    setSubmitting(true);

    try {
      if (!attendanceRecordId) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Attendance record not found",
          timer: 2000,
        });
        setSubmitting(false);
        return;
      }

      // Calculate total work minutes using finish time
      const login = parseTime(loginTime);
      const logout = parseTime(finishTime);

      // Handle case where logout is next day
      let logoutDate = new Date(logout);
      if (logoutDate < login) {
        logoutDate.setDate(logoutDate.getDate() + 1);
      }

      const totalMinutes = (logoutDate - login) / (1000 * 60);
      const breakMinutes = calculateTotalBreakMinutes();
      const totalWorkMinutes = Math.max(0, totalMinutes - breakMinutes);

      const completedBreaks = breaks.filter(b => b.start && b.end);

      // Check if 8.5 hours (510 minutes) work is completed
      const requiredMinutes = 510; // 8.5 hours
      if (totalWorkMinutes < requiredMinutes) {
        const workHours = Math.floor(totalWorkMinutes / 60);
        const workMins = Math.round(totalWorkMinutes % 60);
        const remainingMinutes = requiredMinutes - totalWorkMinutes;
        const remainingHours = Math.floor(remainingMinutes / 60);
        const remainingMins = Math.round(remainingMinutes % 60);

        const result = await Swal.fire({
          title: "Incomplete Work Hours",
          html: `
            <div style="text-align: left; padding: 10px 0;">
              <p style="margin-bottom: 12px; color: #64748b;">You have completed <strong style="color: #0f172a;">${workHours}h ${workMins}m</strong> of work.</p>
              <p style="margin-bottom: 8px; color: #64748b;">Required: <strong style="color: #0f172a;">8h 30m</strong></p>
              <p style="color: #ef4444; font-weight: 500;">Missing: <strong>${remainingHours}h ${remainingMins}m</strong></p>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#ef4444",
          cancelButtonColor: "#64748b",
          confirmButtonText: "Yes, Submit Anyway",
          cancelButtonText: "Cancel",
          reverseButtons: true,
          customClass: {
            popup: "swal2-popup-custom",
            title: "swal2-title-custom"
          }
        });

        if (!result.isConfirmed) {
          setSubmitting(false);
          return;
        }
      }

      // Update existing attendance record with finish time
      await updateDoc(doc(db, "attendance", attendanceRecordId), {
        logoutTime: finishTime,
        breaks: completedBreaks,
        totalWorkMinutes: Math.round(totalWorkMinutes),
      });

      // Mark as submitted
      setAttendanceSubmitted(true);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Attendance finished successfully",
        timer: 2000,
      });

      // Don't reset state or close - show summary instead
      // Keep the data to display in summary view
    } catch (error) {
      console.error("Error finishing attendance:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to finish attendance",
        text: error.message,
        timer: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Regularization Request
  const handleRegularizationRequest = async () => {
    if (!regularizationReason.trim()) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please enter a reason for regularization",
        timer: 2000,
      });
      return;
    }

    setSubmittingRegularization(true);

    try {
      await updateDoc(doc(db, "attendance", attendanceRecordId), {
        regularization: {
          status: "pending",
          reason: regularizationReason.trim(),
          requestedAt: new Date().toISOString(),
        }
      });

      setRegularizationStatus("pending");
      setShowRegularizationInput(false);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Regularization request sent to admin",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error submitting regularization:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to submit request",
        timer: 2000,
      });
    } finally {
      setSubmittingRegularization(false);
    }
  };

  const handleClose = () => {
    // Warn user if there's an active break and attendance is started
    if (activeBreak && attendanceStarted) {
      Swal.fire({
        title: "Active Break Detected",
        text: "You have an active break. Do you want to close without ending it?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, Close",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          // User confirmed, close modal but keep state for next time
          onClose();
        }
      });
    } else {
      // No active break or attendance not started, safe to close
      onClose();
    }
  };

  if (!show) return null;

  // Additional safeguard: If somehow modal is open on mobile, prevent rendering
  if (isMobileDevice()) {
    return null;
  }

  const totalBreakMinutes = calculateTotalBreakMinutes();
  const totalTimeMinutes = calculateTotalTimeMinutes(); // Start to finish (including breaks)
  const calculatedWorkMinutes = calculateTotalWorkMinutes(); // Actual work time (excluding breaks)

  // For submitted attendance, use the saved value from database; otherwise calculate
  const totalWorkMinutes = (attendanceSubmitted && savedTotalWorkMinutes !== null)
    ? savedTotalWorkMinutes
    : calculatedWorkMinutes;

  const breakHours = Math.floor(totalBreakMinutes / 60);
  const breakMins = Math.round(totalBreakMinutes % 60);
  const totalTimeHours = Math.floor(totalTimeMinutes / 60);
  const totalTimeMins = Math.round(totalTimeMinutes % 60);
  const workHours = Math.floor(totalWorkMinutes / 60);
  const workMins = Math.round(totalWorkMinutes % 60);

  // Calculate remaining time to complete 8.5 hours work
  const requiredMinutes = 510; // 8.5 hours
  const remainingMinutes = loginTime && logoutTime ? Math.max(0, requiredMinutes - totalWorkMinutes) : 0;
  const remainingHours = Math.floor(remainingMinutes / 60);
  const remainingMins = Math.round(remainingMinutes % 60);
  const showRemainingWarning = loginTime && logoutTime && remainingMinutes > 0;

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(4px)"
      }}
      tabIndex="-1"
      onClick={(e) => e.target === e.currentTarget && !submitting && handleClose()}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }}>
        <div
          className="modal-content"
          style={{
            borderRadius: "12px",
            border: "none",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            overflow: "hidden"
          }}
        >
          {/* Header */}
          <div
            className="modal-header"
            style={{
              background: "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
              color: "#ffffff",
              borderBottom: "none",
              padding: "16px 20px"
            }}
          >
            <h5
              className="modal-title"
              style={{
                fontSize: "18px",
                fontWeight: "600",
                margin: 0,
                color: "#ffffff",
                letterSpacing: "-0.01em"
              }}
            >
              Mark Attendance
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={submitting}
              style={{ opacity: 0.8 }}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ padding: "20px" }}>
            {/* Shift Hours Info Banner */}
            <div
              style={{
                background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                borderRadius: "10px",
                padding: "12px 16px",
                marginBottom: "16px",
                border: "1px solid #bae6fd",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "16px" }}>🕐</span>
                <div>
                  <div style={{ fontSize: "10px", color: "#0369a1", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em" }}>Shift Start</div>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: "#0c4a6e" }}>1:00 PM</div>
                </div>
              </div>
              <div style={{ width: "1px", height: "30px", background: "#7dd3fc" }}></div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "16px" }}>🕥</span>
                <div>
                  <div style={{ fontSize: "10px", color: "#0369a1", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em" }}>Shift End</div>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: "#0c4a6e" }}>12:00 AM</div>
                </div>
              </div>
              <div style={{ width: "1px", height: "30px", background: "#7dd3fc" }}></div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "16px" }}>⏱️</span>
                <div>
                  <div style={{ fontSize: "10px", color: "#0369a1", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em" }}>Work Hours</div>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: "#0c4a6e" }}>8h 30m</div>
                </div>
              </div>
            </div>

            {/* Summary View - Show when attendance is already submitted */}
            {attendanceSubmitted ? (
              <div>
                {/* Compact Grid Layout */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                    marginBottom: "12px"
                  }}
                >
                  {/* Start Time */}
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "8px",
                      padding: "12px",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <div style={{
                      fontWeight: "500",
                      fontSize: "10px",
                      color: "#64748b",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em"
                    }}>
                      Start Time
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
                      {loginTime || "N/A"}
                    </div>
                  </div>

                  {/* Finish Time */}
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "8px",
                      padding: "12px",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <div style={{
                      fontWeight: "500",
                      fontSize: "10px",
                      color: "#64748b",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em"
                    }}>
                      Finish Time
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
                      {logoutTime || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Breaks - Compact */}
                <div
                  className="mb-3"
                  style={{
                    background: "#ffffff",
                    borderRadius: "8px",
                    padding: "12px",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <div style={{
                    fontWeight: "500",
                    fontSize: "10px",
                    color: "#64748b",
                    marginBottom: "10px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Breaks
                  </div>
                  {breaks.length > 0 ? (
                    <>
                      <div
                        style={{
                          maxHeight: "100px",
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          marginBottom: "10px"
                        }}
                      >
                        {breaks.map((b, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "6px 10px",
                              background: "#f8fafc",
                              borderRadius: "6px",
                              border: "1px solid #e2e8f0",
                              fontSize: "12px"
                            }}
                          >
                            <span style={{
                              color: "#0f172a",
                              fontWeight: "500"
                            }}>
                              #{index + 1} {b.start} - {b.end || "N/A"}
                            </span>
                            {b.start && b.end && (
                              <span style={{
                                fontSize: "11px",
                                color: "#64748b",
                                background: "#ffffff",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontWeight: "500"
                              }}>
                                {Math.round((parseTime(b.end) - parseTime(b.start)) / (1000 * 60))}m
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div
                        style={{
                          padding: "8px 10px",
                          background: "#f1f5f9",
                          borderRadius: "6px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontWeight: "600",
                          color: "#0f172a",
                          fontSize: "12px"
                        }}
                      >
                        <span>Total Breaks:</span>
                        <span>{breaks.length} ({breakHours > 0 ? `${breakHours}h ` : ''}{breakMins}m)</span>
                      </div>
                    </>
                  ) : (
                    <div style={{
                      padding: "10px",
                      background: "#f8fafc",
                      borderRadius: "6px",
                      color: "#64748b",
                      fontSize: "12px",
                      textAlign: "center"
                    }}>
                      No breaks taken
                    </div>
                  )}
                </div>

                {/* Work Hours Comparison */}
                <div
                  className="mb-3"
                  style={{
                    background: totalWorkMinutes < 510
                      ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
                      : "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
                    borderRadius: "10px",
                    padding: "16px",
                    border: totalWorkMinutes < 510 ? "1px solid #fbbf24" : "1px solid #86efac"
                  }}
                >
                  <div style={{
                    fontWeight: "600",
                    fontSize: "11px",
                    color: totalWorkMinutes < 510 ? "#92400e" : "#166534",
                    marginBottom: "14px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    <span>📊</span> Work Hours
                  </div>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px"
                  }}>
                    <div style={{
                      background: "rgba(255,255,255,0.7)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "500", marginBottom: "6px", textTransform: "uppercase" }}>Expected</div>
                      <div style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a" }}>8h 30m</div>
                    </div>
                    <div style={{
                      background: "rgba(255,255,255,0.7)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "500", marginBottom: "6px", textTransform: "uppercase" }}>Actual Worked</div>
                      <div style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: totalWorkMinutes < 510 ? "#dc2626" : "#16a34a"
                      }}>
                        {workHours}h {workMins}m
                      </div>
                    </div>
                  </div>
                  {totalWorkMinutes < 510 && (
                    <div style={{
                      marginTop: "12px",
                      padding: "8px 12px",
                      background: "rgba(255,255,255,0.8)",
                      borderRadius: "6px",
                      fontSize: "11px",
                      color: "#dc2626",
                      fontWeight: "600",
                      textAlign: "center"
                    }}>
                      ⚠️ Missing {Math.floor((510 - totalWorkMinutes) / 60)}h {Math.round((510 - totalWorkMinutes) % 60)}m to complete requirement
                    </div>
                  )}

                  {/* Regularization Section - Only show if work hours < 8h 30m */}
                  {totalWorkMinutes < 510 && (
                    <div style={{ marginTop: "12px" }}>
                      {regularizationStatus === "approved" ? (
                        <div style={{
                          padding: "10px 12px",
                          background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
                          borderRadius: "6px",
                          fontSize: "12px",
                          color: "#166534",
                          fontWeight: "600",
                          textAlign: "center",
                          border: "1px solid #86efac"
                        }}>
                          ✅ Regularised - Hours adjusted to 8h 30m
                        </div>
                      ) : regularizationStatus === "pending" ? (
                        <div style={{
                          padding: "10px 12px",
                          background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                          borderRadius: "6px",
                          fontSize: "12px",
                          color: "#92400e",
                          fontWeight: "600",
                          textAlign: "center",
                          border: "1px solid #fbbf24"
                        }}>
                          ⏳ Regularization Pending Approval
                          {regularizationReason && (
                            <div style={{ fontWeight: "400", marginTop: "4px", fontSize: "11px" }}>
                              Reason: {regularizationReason}
                            </div>
                          )}
                        </div>
                      ) : regularizationStatus === "rejected" ? (
                        <div style={{
                          padding: "10px 12px",
                          background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                          borderRadius: "6px",
                          fontSize: "12px",
                          color: "#dc2626",
                          fontWeight: "600",
                          textAlign: "center",
                          border: "1px solid #fca5a5"
                        }}>
                          ❌ Regularization Rejected
                        </div>
                      ) : showRegularizationInput ? (
                        <div style={{
                          padding: "12px",
                          background: "rgba(255,255,255,0.9)",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0"
                        }}>
                          <label style={{
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#64748b",
                            marginBottom: "8px",
                            display: "block",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em"
                          }}>
                            Reason for Regularization
                          </label>
                          <textarea
                            value={regularizationReason}
                            onChange={(e) => setRegularizationReason(e.target.value)}
                            placeholder="Enter reason for regularization..."
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "6px",
                              border: "1px solid #e2e8f0",
                              fontSize: "13px",
                              minHeight: "80px",
                              resize: "vertical",
                              marginBottom: "10px"
                            }}
                          />
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              onClick={handleRegularizationRequest}
                              disabled={submittingRegularization}
                              style={{
                                flex: 1,
                                padding: "8px 12px",
                                background: "#0ea5e9",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: submittingRegularization ? "not-allowed" : "pointer",
                                opacity: submittingRegularization ? 0.7 : 1
                              }}
                            >
                              {submittingRegularization ? "Submitting..." : "Submit Request"}
                            </button>
                            <button
                              onClick={() => {
                                setShowRegularizationInput(false);
                                setRegularizationReason("");
                              }}
                              style={{
                                padding: "8px 12px",
                                background: "#f1f5f9",
                                color: "#64748b",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer"
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowRegularizationInput(true)}
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px"
                          }}
                        >
                          📝 Request Regularization
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Breaks Comparison */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                    borderRadius: "10px",
                    padding: "16px",
                    border: "1px solid #bae6fd"
                  }}
                >
                  <div style={{
                    fontWeight: "600",
                    fontSize: "11px",
                    color: "#0369a1",
                    marginBottom: "14px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    <span>☕</span> Break Time
                  </div>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px"
                  }}>
                    <div style={{
                      background: "rgba(255,255,255,0.7)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "500", marginBottom: "6px", textTransform: "uppercase" }}>Allotted</div>
                      <div style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a" }}>1h 0m</div>
                    </div>
                    <div style={{
                      background: "rgba(255,255,255,0.7)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "500", marginBottom: "6px", textTransform: "uppercase" }}>Taken</div>
                      <div style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: totalBreakMinutes > 60 ? "#dc2626" : "#0f172a"
                      }}>
                        {breakHours}h {breakMins}m
                      </div>
                    </div>
                  </div>
                  {totalBreakMinutes > 60 && (
                    <div style={{
                      marginTop: "12px",
                      padding: "8px 12px",
                      background: "rgba(254, 202, 202, 0.5)",
                      borderRadius: "6px",
                      fontSize: "11px",
                      color: "#dc2626",
                      fontWeight: "600",
                      textAlign: "center"
                    }}>
                      ⚠️ Exceeded allotted break by {Math.floor((totalBreakMinutes - 60) / 60) > 0 ? `${Math.floor((totalBreakMinutes - 60) / 60)}h ` : ''}{Math.round((totalBreakMinutes - 60) % 60)}m
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Time Section - Compact Grid Layout */}
                <div
                  className="mb-3"
                  style={{
                    display: "grid",
                    gridTemplateColumns: attendanceStarted ? "1fr 1fr" : "1fr",
                    gap: "12px"
                  }}
                >
                  {/* Login Time */}
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "8px",
                      padding: "14px",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <label
                      className="form-label"
                      style={{
                        fontWeight: "500",
                        fontSize: "11px",
                        color: "#64748b",
                        marginBottom: "6px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        display: "block"
                      }}
                    >
                      Login Time
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      value={loginTime}
                      readOnly
                      disabled={submitting}
                      style={{
                        backgroundColor: "transparent",
                        border: "none",
                        borderRadius: "0",
                        padding: "4px 0",
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#0f172a",
                        cursor: "not-allowed",
                        boxShadow: "none"
                      }}
                    />
                  </div>

                  {/* End Time - Only show if attendance is started */}
                  {attendanceStarted && (
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: "8px",
                        padding: "14px",
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <label
                        className="form-label"
                        style={{
                          fontWeight: "500",
                          fontSize: "11px",
                          color: "#64748b",
                          marginBottom: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          display: "block"
                        }}
                      >
                        End Time
                      </label>
                      <div style={{ fontSize: "18px", fontWeight: "600", color: "#64748b" }}>
                        {logoutTime || "Will be set automatically"}
                      </div>
                      {!logoutTime && (
                        <div style={{
                          fontSize: "10px",
                          color: "#94a3b8",
                          marginTop: "4px",
                          fontStyle: "italic"
                        }}>
                          Set when you click Finish
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Break Management - Compact */}
                <div
                  className="mb-3"
                  style={{
                    background: "#ffffff",
                    borderRadius: "8px",
                    padding: "14px",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <label
                      className="form-label"
                      style={{
                        fontWeight: "500",
                        fontSize: "11px",
                        color: "#64748b",
                        margin: 0,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                      }}
                    >
                      Break Management
                    </label>
                    {activeBreak && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "11px",
                          color: "#10b981",
                          fontWeight: "600"
                        }}
                      >
                        <div style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: "#10b981",
                          boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.2)"
                        }}></div>
                        Active: {activeBreak.start}
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn flex-fill"
                      onClick={handleStartBreak}
                      disabled={submitting || activeBreak !== null || attendanceSubmitted || !attendanceStarted}
                      style={{
                        background: (activeBreak === null && attendanceStarted) ? "#111827" : "#f1f5f9",
                        color: (activeBreak === null && attendanceStarted) ? "#ffffff" : "#94a3b8",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        fontWeight: "500",
                        fontSize: "13px",
                        transition: "all 0.15s ease",
                        cursor: (activeBreak === null && !attendanceSubmitted && attendanceStarted) ? "pointer" : "not-allowed"
                      }}
                      onMouseEnter={(e) => {
                        if (activeBreak === null && !attendanceSubmitted && attendanceStarted) {
                          e.target.style.background = "#374151";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeBreak === null && !attendanceSubmitted && attendanceStarted) {
                          e.target.style.background = "#111827";
                        }
                      }}
                    >
                      Start Break
                    </button>
                    <button
                      className="btn flex-fill"
                      onClick={handleEndBreak}
                      disabled={submitting || activeBreak === null || attendanceSubmitted}
                      style={{
                        background: activeBreak !== null ? "#111827" : "#f1f5f9",
                        color: activeBreak !== null ? "#ffffff" : "#94a3b8",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        fontWeight: "500",
                        fontSize: "13px",
                        transition: "all 0.15s ease",
                        cursor: (activeBreak !== null && !attendanceSubmitted) ? "pointer" : "not-allowed"
                      }}
                      onMouseEnter={(e) => {
                        if (activeBreak !== null) {
                          e.target.style.background = "#374151";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeBreak !== null) {
                          e.target.style.background = "#111827";
                        }
                      }}
                    >
                      End Break
                    </button>
                  </div>
                </div>

                {/* Break Summary - Compact */}
                {breaks.length > 0 && (
                  <div
                    className="mb-3"
                    style={{
                      background: "#f8fafc",
                      borderRadius: "8px",
                      padding: "12px",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px"
                    }}>
                      <span style={{
                        fontWeight: "500",
                        fontSize: "11px",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                      }}>
                        Breaks ({breaks.length})
                      </span>
                      {totalBreakMinutes > 0 && (
                        <span style={{
                          fontSize: "12px",
                          color: "#475569",
                          fontWeight: "600"
                        }}>
                          Total: {breakHours > 0 ? `${breakHours}h ` : ''}{breakMins}m
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        maxHeight: "100px",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px"
                      }}
                    >
                      {breaks.map((b, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 10px",
                            background: "#ffffff",
                            borderRadius: "6px",
                            border: "1px solid #e2e8f0",
                            fontSize: "12px"
                          }}
                        >
                          <span style={{
                            color: "#0f172a",
                            fontWeight: "500",
                            fontSize: "12px"
                          }}>
                            #{index + 1} {b.start} - {b.end || <span style={{ color: "#10b981" }}>Active</span>}
                          </span>
                          {b.start && b.end && (
                            <span style={{
                              fontSize: "11px",
                              color: "#64748b",
                              background: "#f1f5f9",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontWeight: "500"
                            }}>
                              {Math.round((parseTime(b.end) - parseTime(b.start)) / (1000 * 60))}m
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warning Banner - Remaining Hours */}
                {showRemainingWarning && (
                  <div
                    className="mb-3"
                    style={{
                      background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                      borderRadius: "8px",
                      padding: "14px 16px",
                      border: "1px solid #fbbf24",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)"
                    }}
                  >
                    <div style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "#f59e0b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}>
                      <span style={{ color: "#ffffff", fontSize: "14px", fontWeight: "bold" }}>!</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#92400e",
                        marginBottom: "2px"
                      }}>
                        Incomplete Working Hours
                      </div>
                      <div style={{
                        fontSize: "12px",
                        color: "#78350f",
                        fontWeight: "500"
                      }}>
                        Still remaining: <strong style={{ fontSize: "13px" }}>{remainingHours}h {remainingMins}m</strong> to complete 8h 30m working hours
                      </div>
                    </div>
                  </div>
                )}

                {/* Work Summary - Compact */}
                {loginTime && logoutTime && (
                  <div
                    style={{
                      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      borderRadius: "8px",
                      padding: "16px",
                      border: "1px solid #e2e8f0",
                      marginTop: "4px"
                    }}
                  >
                    <div style={{
                      fontWeight: "500",
                      fontSize: "11px",
                      color: "#64748b",
                      marginBottom: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em"
                    }}>
                      Work Summary
                    </div>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: totalBreakMinutes > 0 ? "1fr 1fr" : "1fr",
                      gap: "16px"
                    }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px", fontWeight: "400" }}>
                          Total Work Time
                        </div>
                        <div style={{ fontSize: "22px", fontWeight: "700", color: "#0f172a", letterSpacing: "-0.02em" }}>
                          {workHours}h {workMins}m
                        </div>
                      </div>
                      {totalBreakMinutes > 0 && (
                        <div>
                          <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px", fontWeight: "400" }}>
                            Total Break Time
                          </div>
                          <div style={{ fontSize: "22px", fontWeight: "700", color: "#0f172a", letterSpacing: "-0.02em" }}>
                            {breakHours}h {breakMins}m
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className="modal-footer"
            style={{
              borderTop: "1px solid #e2e8f0",
              padding: "16px 20px",
              background: "#ffffff",
              display: "flex",
              gap: "10px",
              justifyContent: attendanceSubmitted ? "flex-end" : "flex-end"
            }}
          >
            {attendanceSubmitted ? (
              <button
                type="button"
                className="btn"
                onClick={handleClose}
                style={{
                  background: "#111827",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 24px",
                  fontWeight: "600",
                  fontSize: "13px",
                  transition: "all 0.15s ease",
                  minWidth: "120px"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#374151";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#111827";
                }}
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={handleClose}
                  disabled={submitting}
                  style={{
                    background: "#ffffff",
                    color: "#475569",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontWeight: "500",
                    fontSize: "13px",
                    transition: "all 0.15s ease",
                    minWidth: "80px"
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.target.style.background = "#f8fafc";
                      e.target.style.borderColor = "#94a3b8";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.background = "#ffffff";
                      e.target.style.borderColor = "#cbd5e1";
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={attendanceStarted ? handleFinishAttendance : handleStartAttendance}
                  disabled={
                    submitting ||
                    !loginTime
                  }
                  style={{
                    background: submitting || !loginTime
                      ? "#f1f5f9"
                      : "#111827",
                    color: submitting || !loginTime
                      ? "#94a3b8"
                      : "#ffffff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 20px",
                    fontWeight: "600",
                    fontSize: "13px",
                    transition: "all 0.15s ease",
                    minWidth: "160px",
                    cursor: submitting || !loginTime
                      ? "not-allowed"
                      : "pointer"
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting && loginTime) {
                      e.target.style.background = "#374151";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting && loginTime) {
                      e.target.style.background = "#111827";
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" style={{ width: "12px", height: "12px", borderWidth: "2px" }}></span>
                      {attendanceStarted ? "Finishing..." : "Starting..."}
                    </>
                  ) : attendanceStarted ? (
                    "Finish Attendance"
                  ) : (
                    "Start Attendance"
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceModal;

